import React from 'react';

import { Bone } from '@lightspeed/flame/Bone';

import { LabelSize } from 'label/utils/types';

export const labelSizes: {
  [key in LabelSize]: {
    printWidth: number;
    printHeight: number;
  };
} = {
  [LabelSize.NORMAL]: {
    printWidth: 456,
    printHeight: 254,
  },
  [LabelSize.ALT]: {
    printWidth: 406,
    printHeight: 203,
  },
  [LabelSize.SMALL]: {
    printWidth: 254,
    printHeight: 203,
  },
  [LabelSize.JEWELRY]: {
    printWidth: 446,
    printHeight: 99,
  },
};

interface LabelOutlineProps {
  addBoxShadow?: boolean;
  style?: React.HTMLAttributes<HTMLOrSVGScriptElement>['style'];
  scale?: number;
  size: LabelSize;
  path?: JSX.Element[] | undefined;
}

interface SVGProps {
  width: number;
  height: number;
  style?: React.HTMLAttributes<HTMLOrSVGScriptElement>['style'];
  path?: JSX.Element[] | undefined;
}

const Regular: React.FC<SVGProps> = ({ width, height, style, path }) => (
  <svg
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    viewBox={`0 0 ${width + 1} ${height + 1}`}
    height={height + 1}
    width={width + 1}
  >
    <rect
      height={height}
      width={width}
      x="0.5"
      y="0.5"
      rx="10"
      ry="10"
      fill="white"
      stroke="#464A51"
      strokeWidth="5"
    />
    {path}
  </svg>
);

const Jewelry: React.FC<SVGProps> = ({ width, height, style, path }) => (
  <svg
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
    viewBox="1 1 440 101"
    width={width}
    height={height + 2}
  >
    <path
      d="M14.0229 1.49904C7.10618 1.49904 1.49904 7.10618 1.49904 14.0229V88.9771C1.49904 95.8938 7.10618 101.501 14.0229 101.501H111.5H141.842C148.453 101.501 154.659 98.3193 158.518 92.9523L169.629 77.5002C171.793 74.4894 175.275 72.7046 178.983 72.7046H221H262.937C266.645 72.7046 270.127 74.4894 272.292 77.5002L283.402 92.9523C287.261 98.3193 293.468 101.501 300.078 101.501H330.5H427.977C434.894 101.501 440.501 95.8938 440.501 88.9771V14.0229C440.501 7.10618 434.894 1.49904 427.977 1.49904H330.5H300.983C293.862 1.49904 287.249 5.18765 283.507 11.2467L272.198 29.561C270.099 32.9599 266.389 35.0292 262.394 35.0292H221H179.526C175.531 35.0292 171.822 32.9599 169.723 29.561L158.413 11.2467C154.672 5.18765 148.059 1.49904 140.938 1.49904H111.5H14.0229Z"
      fill="white"
      stroke="#D7DCE1"
      vectorEffect="non-scaling-stroke"
    />
    {path}
  </svg>
);

const LabelOutline = ({ style, size, scale = 1, addBoxShadow, path }: LabelOutlineProps) => {
  const styleWithDrop = {
    ...style,
    filter: `${addBoxShadow ? 'drop-shadow(0px 1px 2px rgb(0 0 0 / 0.1))' : 'none'}`,
  };
  const { printHeight, printWidth } = labelSizes[size] || {};

  switch (size) {
    case LabelSize.NORMAL:
    case LabelSize.ALT:
    case LabelSize.SMALL:
      return (
        <Regular
          height={printHeight * scale}
          width={printWidth * scale}
          style={styleWithDrop}
          path={path}
        />
      );
    case LabelSize.JEWELRY:
      return (
        <Jewelry
          height={printHeight * scale}
          width={printWidth * scale}
          style={styleWithDrop}
          path={path}
        />
      );

    default:
      return <Bone height={4} width={6} />;
  }
};

export default LabelOutline;
